import { useQuery } from "react-query";
import React from "react";
import { toast } from "react-toastify";
import { API } from "../../utils/AxiosInstance";
import {
    RazorpayPaymentSuccess,
    CreateSubscriptionResponse,
    RazorpayOptions,
    PlansResponse,
    SubscriptionLinkResponse,
    RazorpayPaymentParams,
} from "./types";

export function useSubscriptionLink(
    planId: string,
    jotformId: string,
    userId: string,
    enabled = true
) {
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<SubscriptionLinkResponse | null>(
        null
    );
    const [error, setError] = React.useState<string | null>(null);
    const [loadingMsgIdx, setLoadingMsgIdx] = React.useState(0);
    const loadingMessages = [
        "Checking if the User Sync completed and fetching info...",
        "Still syncing user data, please wait...",
        "Almost there, verifying user sync...",
        "Sync in progress, fetching your payment info...",
        "Final attempt to fetch user subscription info...",
    ];

    const fetchData = React.useCallback(
        (retryCount = 1) => {
            setLoading(true);
            setError(null);
            setLoadingMsgIdx(retryCount - 1);
            API.post<SubscriptionLinkResponse>(
                "/razorpay/get_user_subscription_url",
                {
                    plan_id: planId,
                    jotform_id: jotformId,
                    user_id: userId,
                }
            )
                .then((response) => {
                    const respData = response.data;
                    if (respData.success && !respData.error) {
                        setData(respData);
                        setLoading(false);
                        setError(null);
                    } else if (respData.error) {
                        if (retryCount < 5) {
                            setTimeout(() => {
                                fetchData(Number(retryCount) + 1);
                            }, 5000);
                        } else {
                            setLoading(false);
                            setError(
                                respData.error === "User not found"
                                    ? "User does not exist in our system"
                                    : "User data is not synced, contact Habitnu support"
                            );
                        }
                    } else {
                        setLoading(false);
                        setError(
                            "User data is not synced, contact Habitnu support"
                        );
                    }
                })
                .catch(() => {
                    setLoading(false);
                    setError(
                        "User data is not synced, contact Habitnu support"
                    );
                });
        },
        [planId, jotformId, userId]
    );

    React.useEffect(() => {
        if (!planId || !(jotformId || userId) || !enabled) return;
        setData(null);
        setError(null);
        setLoading(true);
        setLoadingMsgIdx(0);
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planId, jotformId, userId, enabled]);

    const refetch = React.useCallback(() => {
        setData(null);
        setError(null);
        setLoading(true);
        setLoadingMsgIdx(0);
        fetchData(1);
    }, [fetchData]);

    return {
        data,
        isLoading: loading,
        error,
        loadingMsg: loadingMessages[loadingMsgIdx] || loadingMessages[0],
        refetch,
    };
}

export function useRazorpayPayment({
    subscriptionData,
    userDetails,
    refetch,
    setShowThankYou,
    setThankYouState,
    planName,
    setSubscriptionLoading,
}: RazorpayPaymentParams) {
    async function loadScript(src: string): Promise<boolean> {
        return new Promise((resolve) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve(true);
                return;
            }
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    }

    // Add a helper to mask email
    // function maskEmail(email: string): string {
    //     const [local, domain] = email.split("@");
    //     if (!local || !domain) return email;
    //     if (local.length <= 6) {
    //         return `${local[0]}${"*".repeat(
    //             Math.max(0, local.length - 2)
    //         )}${local.slice(-1)}@${domain}`;
    //     }
    //     return `${local.slice(0, 3)}${"*".repeat(
    //         local.length - 6
    //     )}${local.slice(-3)}@${domain}`;
    // }

    const displayRazorpay = async () => {
        if (!subscriptionData?.plan_details) {
            toast.error("Plan details not loaded.");
            return;
        }
        const scriptLoaded = await loadScript(
            "https://checkout.razorpay.com/v1/checkout.js"
        );
        if (!scriptLoaded) {
            toast.error("Razorpay failed to load");
            return;
        }
        let subscriptionId = subscriptionData.subscription_id;
        let sessionId: string | undefined;
        if (!subscriptionId) {
            try {
                const createRes = await API.post<CreateSubscriptionResponse>(
                    "/razorpay/subscriptions",
                    {
                        plan_id: subscriptionData.plan_details.id,
                        customer: {
                            name: userDetails?.name || "Subscriber",
                            email: userDetails?.email || "",
                            phone: userDetails?.contact || "",
                        },
                        total_count: 12,
                    }
                );
                if (
                    !createRes.data.success ||
                    !createRes.data.subscription?.id
                ) {
                    toast.error(
                        createRes.data.error || "Failed to create subscription"
                    );
                    return;
                }
                subscriptionId = createRes.data.subscription.id;
                sessionId = createRes.data.session_id;
                refetch();
            } catch {
                toast.error("Could not create subscription. Try again.");
                return;
            }
        }
        const options: RazorpayOptions = {
            key: (import.meta.env.VITE_APP_RAZORPAY_KEY_ID) || "",
            subscription_id: subscriptionId || "",
            name: "HabitNu",
            description:
                subscriptionData.plan_details.description ||
                `Subscription: ${subscriptionId}`,
            image: "/images/habitnu/small-logo.svg",
            prefill: {
                name: userDetails?.name,
                email: userDetails?.email,
                contact: userDetails?.contact,
            },
            notes: {
                subscription_id: subscriptionId || "",
                plan_id: subscriptionData.plan_details.id,
                source: "react_subscription_checkout",
            },
            notify_info: {
                notify_email: userDetails?.email,
                notify_phone: userDetails?.contact,
            },
            customer_notify: 1,
            theme: {
                color: "#00087a",
            },
            ...(sessionId ? { session_id: sessionId } : {}),
            handler: async (payment: RazorpayPaymentSuccess) => {
                const notifyPayload = {
                    payment,
                    plan_id: subscriptionData?.plan_details?.id,
                    subscription_id: subscriptionId,
                    customer_id: subscriptionData?.customer_id,
                    email: userDetails?.email || "",
                    user_details: subscriptionData?.user_details,
                };

                // Recursive polling function
                async function pollSubscriptionActive(
                    attempt = 1
                ): Promise<boolean> {
                    setSubscriptionLoading?.(true);
                    try {
                        type SubscribedResponse = {
                            subscription?: { status?: string };
                            status?: string;
                        };
                        const resp = await API.post<SubscribedResponse>(
                            "/razorpay/subscribed",
                            notifyPayload
                        );
                        const status =
                            resp.data?.subscription?.status ??
                            resp.data?.status;
                        if (status === "active") {
                            return true;
                        }
                    } catch {
                        // Ignore errors and retry
                    }
                    if (attempt < 25) {
                        await new Promise<void>((resolve) => {
                            setTimeout(resolve, 1000);
                        });
                        return pollSubscriptionActive(attempt + 1);
                    }
                    setSubscriptionLoading?.(false);
                    // Instead of toast, render SubscriptionActivationPending page
                    window.location.replace(
                        "/india-payment/subscription-activation-pending"
                    );
                    refetch();
                    return false;
                }

                let isActive = false;
                try {
                    isActive = await pollSubscriptionActive();
                } catch {
                    isActive = false;
                }

                if (isActive) {
                    setThankYouState({
                        email: userDetails?.email || "",
                        planName,
                        from: "payment",
                    });
                    setShowThankYou(true);
                    setSubscriptionLoading?.(false);
                } else {
                    setSubscriptionLoading?.(false);
                    // Instead of toast, render SubscriptionActivationPending page
                    window.location.replace(
                        "/india-payment/subscription-activation-pending"
                    );
                    refetch();
                }
            },
        };
        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
    };

    return { displayRazorpay };
}

export function usePlans() {
    return useQuery<PlansResponse, Error>(
        ["razorpay-plans"],
        async () => {
            const res = await API.get<PlansResponse>("/razorpay/plans");
            return res.data;
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
        }
    );
}
